import React from "react";
import { motion } from "framer-motion";
import { FaClock, FaVideo, FaHeart, FaMapMarkerAlt } from "react-icons/fa";

const HowItWorksSection = () => {
  const steps = [
    {
      id: 1,
      icon: <FaClock className="text-4xl" />,
      title: "Join at 7pm",
      description:
        " Every evening at 7pm, singles from Bournemouth come together for an exciting video dating experience—your datenite.",
      color: "from-romantic-purple to-romantic-blue",
    },
    {
      id: 2,
      icon: <FaVideo className="text-4xl" />,
      title: "2-min video speed dates",
      description:
        "Quick, fun video calls that let you see the real person behind the profile—no ghosting, no swiping, or endless texting!",
      color: "from-romantic-blue to-romantic-pink",
    },
    {
      id: 3,
      icon: <FaHeart className="text-4xl" />,
      title: "Match & meet in real life",
      description:
        "Found someone special? Match and meet at a partner venue in Bournemouth—datenite offers await, with some exclusive to you.",
      color: "from-romantic-pink to-yellow-400",
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.1,
        staggerChildren: 0.08,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  return (
    <section className="py-20 relative w-full">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          {/* Section Header */}
          <motion.div variants={itemVariants} className="text-center mb-16">
            <motion.div
              className="inline-flex items-center gap-2 mb-4"
              whileHover={{ scale: 1.05 }}
            >
              <FaMapMarkerAlt className="text-romantic-pink text-2xl" />
              <span className="text-romantic-pink font-semibold text-lg">
                Bournemouth
              </span>
            </motion.div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-3">
            Between 7pm-10pm / Each Night,
            </h2>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              <span className=" gradient-text">
                Meet Singles from Bournemouth
              </span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Join the revolution in dating. Real connections, real
              conversations, real meetings.
            </p>
          </motion.div>

          {/* Steps */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-16 items-stretch">
            {steps.map((step, index) => (
              <motion.div
                key={step.id}
                variants={itemVariants}
                className="relative h-full"
                whileHover={{ y: -10 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                {/* Connection Line */}
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 w-8 h-0.5 bg-gradient-to-r from-romantic-purple to-romantic-pink transform -translate-y-1/2 z-10" />
                )}

                <div className="glass rounded-2xl p-8 text-center relative overflow-hidden group h-full min-h-[260px] md:min-h-[280px] flex flex-col">
                  {/* Background Gradient */}
                  <div
                    className={`absolute inset-0 bg-gradient-to-br ${step.color} opacity-10 group-hover:opacity-20 transition-opacity duration-300`}
                  />

                  {/* Step Number */}
                  <div className="absolute top-4 right-4 w-8 h-8 bg-gradient-to-br from-romantic-purple to-romantic-pink rounded-full flex items-center justify-center text-white font-bold text-sm">
                    {step.id}
                  </div>

                  {/* Icon */}
                  <motion.div
                    className={`inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br ${step.color} text-white mb-6 relative z-10`}
                    whileHover={{ rotate: 360, scale: 1.1 }}
                    transition={{ duration: 0.6 }}
                  >
                    {step.icon}
                  </motion.div>

                  {/* Content */}
                  <h3 className="text-2xl font-bold text-white mb-4 relative z-10">
                    {step.title}
                  </h3>
                  <p className="text-gray-300 leading-relaxed relative z-10">
                    {step.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Visual Representation */}
          <motion.div
            variants={itemVariants}
            className="flex justify-center items-center gap-8 flex-wrap"
          >
            {/* Phone Frame Visualization */}
            <div className="grid grid-cols-2 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <motion.div
                  key={i}
                  className="w-42 h-52 shadow-lg"
                  animate={{
                    scale: [1, 1.05, 1],
                    rotate: [0, 1, -1, 0],
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    delay: i * 0.5,
                  }}
                >
                  <img 
                    src={`/assets/phone-frem-${i}.png`}
                    alt={`Phone frame ${i}`}
                    className="w-full h-full object-contain"
                  />
                </motion.div>
              ))}
            </div>

            {/* Arrow */}
            <motion.div
              className="text-romantic-pink text-4xl"
              animate={{ x: [0, 10, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              →
            </motion.div>

            {/* Heart Connection */}
            <motion.div
              className="flex items-center gap-2"
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <FaHeart className="text-romantic-pink text-3xl" />
              <span className="text-white font-semibold">Real Connections</span>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
